import { useEffect, useRef, useState } from 'react';
import { formatDateText, formatTimeText, isCompleteDateText, parseDateText, parseTimeText } from '@bingo/shared';
import { IconCalendar } from './Icons';
import { useT } from '../lib/i18n';

/*
 * Date and time fields that look the same on every computer: DD/MM/YYYY and 24-hour HH:MM.
 * The browser's own type="date" and type="time" fields follow the computer's regional
 * settings (a PC set to the United States showed 11/04/2026 for 4 November).
 */

interface DateInputProps {
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
}

/** Date typed as DD/MM/YYYY, with a calendar button. `value` and `onChange` use YYYY-MM-DD. */
export function DateInput({ value, onChange, id, required, disabled, 'aria-label': ariaLabel }: DateInputProps) {
  const t = useT();
  const [text, setText] = useState(formatDateText(value));
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => setText(formatDateText(value)), [value]);

  const fallbackYear = Number((value || '').slice(0, 4)) || new Date().getFullYear();

  const commit = () => {
    const iso = parseDateText(text, fallbackYear);
    if (iso) {
      setText(formatDateText(iso));
      if (iso !== value) onChange(iso);
    } else {
      setText(formatDateText(value)); // not a real date: back to the last good one
    }
  };

  const openPicker = () => {
    const el = picker.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
      el.click();
    }
  };

  return (
    <div className="date-field">
      <input
        id={id}
        className="input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={10}
        placeholder={t('DD/MM/YYYY')}
        aria-label={ariaLabel}
        required={required}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          if (isCompleteDateText(v)) {
            const iso = parseDateText(v);
            if (iso && iso !== value) onChange(iso);
          }
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          // A partly typed date is completed first; the form is sent with the next Enter.
          if (parseDateText(text, fallbackYear) !== value) e.preventDefault();
          commit();
        }}
      />
      <button type="button" className="date-field-btn" onClick={openPicker} disabled={disabled}
        aria-label={t('Open the calendar')} title={t('Open the calendar')}>
        <IconCalendar />
      </button>
      <input
        ref={picker}
        className="date-field-picker"
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ''}
        onChange={(e) => e.target.value && onChange(e.target.value)}
      />
    </div>
  );
}

interface TimeInputProps {
  value: string;
  onChange: (hhmm: string) => void;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

/** Time typed in 24 hours (08:30, 8h30, 830...). `value` and `onChange` use HH:MM; empty means no time. */
export function TimeInput({ value, onChange, id, disabled, 'aria-label': ariaLabel }: TimeInputProps) {
  const t = useT();
  const [text, setText] = useState(formatTimeText(value));

  useEffect(() => setText(formatTimeText(value)), [value]);

  const commit = () => {
    if (!text.trim()) {
      if (value) onChange('');
      return;
    }
    const hhmm = parseTimeText(text);
    if (hhmm) {
      setText(hhmm);
      if (hhmm !== formatTimeText(value)) onChange(hhmm);
    } else {
      setText(formatTimeText(value));
    }
  };

  return (
    <input
      id={id}
      className="input time-field"
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      placeholder={t('HH:MM')}
      aria-label={ariaLabel}
      disabled={disabled}
      value={text}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        if (/^\d{2}:\d{2}$/.test(v)) {
          const hhmm = parseTimeText(v);
          if (hhmm && hhmm !== formatTimeText(value)) onChange(hhmm);
        }
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        if ((parseTimeText(text) ?? '') !== formatTimeText(value)) e.preventDefault();
        commit();
      }}
    />
  );
}
